/**
 * Two-way conversation inbox (tracker item 56, plans/modules/56_CONVERSATION_INBOX.md).
 * Mirrors backend conversations.types.ts responses. Re-exported from types.ts.
 */

/** conversations.status. UNMATCHED: inbound mail with no customer (the Unmatched tab). */
export const CONVERSATION_STATUSES = ['OPEN', 'WAITING_ON_CUSTOMER', 'CLOSED', 'UNMATCHED'] as const;
export type ConversationStatus = (typeof CONVERSATION_STATUSES)[number];

/** The statuses staff set and filter the main list by. */
export const STAFF_CONVERSATION_STATUSES = ['OPEN', 'WAITING_ON_CUSTOMER', 'CLOSED'] as const;
export type StaffConversationStatus = (typeof STAFF_CONVERSATION_STATUSES)[number];

export type ConversationDirection = 'INBOUND' | 'OUTBOUND' | 'NOTE';
export type ConversationChannel = 'EMAIL' | 'PORTAL' | 'INTERNAL';

/** CompanySettings.inboxAccess: STAFF = ADMIN + OFFICE_MANAGER; EVERYONE = technicians too. */
export const INBOX_ACCESS_VALUES = ['STAFF', 'EVERYONE'] as const;
export type InboxAccess = (typeof INBOX_ACCESS_VALUES)[number];

export interface Conversation {
  id: string;
  status: ConversationStatus;
  subject: string;
  customer: { id: string; name: string; email: string | null } | null;
  /** The customer opted out of emails: staff can answer only once they wrote in this conversation */
  customerOptedOut: boolean;
  jobId: string | null;
  invoiceId: string | null;
  estimateId: string | null;
  assignedUserId: string | null;
  assignee: { id: string; name: string } | null;
  lastMessageAt: string;
  lastInboundAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** The signed-in user has unread customer messages in it */
  unread: boolean;
  /** EMAIL: by email; PORTAL: a portal user's thread */
  origin: 'EMAIL' | 'PORTAL';
  portalUserId: string | null;
  /** UNMATCHED mail held because the sender is not on that conversation's customer */
  heldForConversationId: string | null;
  discardedAt: string | null;
}

export interface InboundAttachmentsSummary {
  saved: { attachmentId: string; filename: string }[];
  dropped: { filename: string; reason: string }[];
}

export interface MessageDelivery {
  outboxId: string;
  /** PENDING, SENDING, SENT, FAILED, SKIPPED */
  status: string;
  skipReason: string | null;
  skipReasonText: string | null;
  /** DELIVERED, BOUNCED, COMPLAINED, FAILED (provider webhooks) */
  deliveryStatus: string | null;
  sentAt: string | null;
}

export interface ConversationMessage {
  id: string;
  conversationId: string;
  direction: ConversationDirection;
  channel: ConversationChannel;
  fromAddress: string | null;
  toAddress: string | null;
  /** As received / written */
  bodyText: string;
  /** Inbound: without quoted history and signature (show this); otherwise the body */
  displayText: string;
  /** Sanitized HTML part of inbound mail (never rendered by this app) */
  html: string | null;
  isUnsubscribeRequest: boolean;
  attachments: InboundAttachmentsSummary | null;
  authorUserId: string | null;
  authorName: string | null;
  portalUserId: string | null;
  isAutoReply: boolean;
  createdAt: string;
  delivery: MessageDelivery | null;
}

export interface ConversationDetail {
  conversation: Conversation;
  /** Oldest first */
  messages: ConversationMessage[];
}

export interface ConversationPage<T = Conversation> {
  data: T[];
  pagination: { page: number; limit: number; total: number; pages: number };
}

export interface ConversationListQuery {
  status?: StaffConversationStatus | '';
  /** "me", "none" or a user id */
  assignee?: string;
  customerId?: string;
  jobId?: string;
  unread?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface UnmatchedConversation extends Conversation {
  from: string | null;
  preview: string;
  messageCount: number;
}

export interface UnreadCounts {
  total: number;
  assignedToMe: number;
}

export interface CreateConversationInput {
  customerId: string;
  subject: string;
  body: string;
  jobId?: string | null;
  idempotencyKey?: string;
}

export interface ConversationSendResult {
  conversation: Conversation;
  message: ConversationMessage;
  /** The same idempotencyKey was used before: the earlier message, nothing new sent */
  deduplicated: boolean;
}

/** PATCH /conversations/:id: only the fields sent change; null unassigns / unlinks. */
export interface UpdateConversationInput {
  status?: StaffConversationStatus;
  assignedUserId?: string | null;
  jobId?: string | null;
  customerId?: string;
  subject?: string;
}

/** POST /conversations/:id/assign-customer: one of the two. */
export type AssignCustomerInput = { customerId: string } | { conversationId: string };

// ---- Portal (phase 3)

export interface PortalMessage {
  id: string;
  from: 'CUSTOMER' | 'STAFF';
  body: string;
  /** The staff member's first name (STAFF only), or null */
  authorName: string | null;
  createdAt: string;
}

export interface PortalThread {
  conversationId: string | null;
  messages: PortalMessage[];
  unreadCount: number;
}
